/**
 * Описание типов для СОПР 1.0
 * статья: https://cianru.atlassian.net/wiki/spaces/ML/pages/443613219
 * сваггер-схема https://bitbucket.org/cianmedia/sopr-events-validation/src/master/sopr_events_validation/site.yaml
 * window.ca('eventSite', data)  - пойдет в ручку /site
 * в page.setParameters рядом с cianAnalyticsPageViewObject, добавить флаг cianAnalyticsPageViewObjectNewFormat: true
 *
 * В метке required перечислены обязательные поля для апи,
 * но в конкретном мкс обязательных полей больше,
 * поэтому в самом интерфейсе обязательных полей соответственно больше.
 * Следите за актуальностью обязательных полей самостоятельно при копировании!
 *
 * cianUserId это id в userData
 * RealtyID это userId в userData
 */

/**
 * Тип пользователя
 */
export enum ESopr1UserTypes {
  not_authorized = 'not_authorized', // не авторизованный
  realtor_based = 'realtor_based', // с тарифом базовый
  realtor_not_commerce = 'realtor_not_commerce', // без платного тарифа, но подавал объявления
  realtor_prof = 'realtor_prof', // с тарифом Профи
  physical = 'physical', // зарегистрирован, но не добавлял объявления
  developer = 'developer', // застройщик
  searcher = 'searcher', // поискун
  specialist = 'specialist', // есть в сваггер-схеме, но нет в статье
  managementCompany = 'managementCompany', // есть в статье, но нет в сваггер-схеме
  rentDepartment = 'rentDepartment', // есть в статье, но нет в сваггер-схеме
}

// Вид выдачи.
export enum EListingType {
  table = 'table', // табличная
  list = 'list', // табличная
  lk = 'lk', // в личном кабинете
  map = 'map', // в карте (после клика на пин)
  mapAuto = 'mapAuto', // в карте (авто-раскрытие)
}

// тип сортировки
export enum ESortType {
  default = 'default',
  price = 'price',
  street_name = 'street_name',
  id_user = 'id_user',
  area_order = 'area_order',
  price_square_order = 'price_square_order',
}

// Тип страницы
export enum EPageType {
  Card = 'Card', // Карточка (объявление)
  CardJK = 'CardJK', // Карточка ЖК
  CardBC = 'CardBC', // Карточка БЦ
  CardTC = 'CardTC', // Карточка ТЦ
  CardKP = 'CardKP', // Карточка КП
  Listing = 'Listing', // Выдача объявлений (обычная и внутри агентства)
  ListingJK = 'ListingJK', // Выдача ЖК (обычная и внутри застройщика)
  ListingBC = 'ListingBC', // Выдача БЦ
  ListingTC = 'ListingTC', // Выдача ТЦ
  Other = 'Other', // Другое
}

// Тип сайта
export enum ESiteType {
  desktop = 'desktop',
  mobile = 'mobile',
  tablet = 'tablet',
}

// Тип объекта
export enum EObjectType {
  flatOldNew = 'flat_old,flat_new', // квартира без указания типа новостройка/вторичка
  flatNew = 'flat_new', // только  новостройка жилая
  flatNewDev = 'flat_new_dev', // квартира от застройщика
  flatOld = 'flat_old', // только  вторичка жилая
  commercial = 'commercial', // коммерческая недвижимость
  suburban = 'suburban', // загородная недвижимость
  jk = 'JK', // жилой комплекс
  bc = 'BC', // бизнес-центр
  tc = 'TC', // торговый центр
  kp = 'KP', // коттеджный посёлок
  other = 'other', // Другое
}

// Тип сделки
export enum EDealType {
  rent_day = 'rent_day', // посуточная аренда
  rent_long = 'rent_long', // длительная аренда
  sale = 'sale', // продажа
  other = 'other', // другое
}

// RealtyID объявления для CriteoProducts в блоке Partner
interface ICriteoProduct {
  type: number;
  format: number;
}

interface IPageViewExtra extends Record<string, unknown> {
  suburban_aa_web_with_exclusion: string | null | undefined;
  suburban_aa_web_without_exclusion: string | null | undefined;
}

export interface ISopr1UTM {
  campaign: string;
  content: string;
  medium: string;
  source: string;
  term: string;
}

/**
 * Объявления, связанные с событием.
 * Обязательно, если такие есть.
 * Например, при звонке, расхлопе, просмотре карточки,
 * клике по рекомендациям - массив из одного элемента,
 * над которым производилось действие (расхлоп, звонок, показ, клик).
 * При показе рекомендаций, поисковой выдаче - показанные рекомендации, результаты поиска.
 *
 * Для всех событий просмотров карточек объектов вдобавок к требованиям
 * необходимо заполнятьполе $.products массивом из одного элемента - объектом,
 * карточка которого была просмотрена
 *
 * @required: [id, dealType, objectType, position, brand]
 */
export interface ISopr1Product {
  id: number; // RealtyID объявления
  cianId?: number; // CianID объявления
  parentId?: number; // ID ЖК/ТЦ/БЦ к которому привязано объявление
  ownerId?: number; // RealtyID владельца объявления
  ownerCianId?: number; // CianID владельца объявления
  dealType: EDealType; // Тип сделки
  objectType: EObjectType; // Тип объекта
  name?: string; // Название объявления
  position: number; // Позиция объекта
  // Аукционная позиция объявления. Обязательно для событий, связанных с аукционными объявлениями на выдаче.
  auctionPosition?: number;
  published?: boolean; // Флаг активности объявления
  price?: number; // Цена объекта
  brand: string; // ID автора или застройщика/тип пользователя 'int64/`user_type`'
  photosCount?: number; // Количество фотографий
  hiddenBase?: boolean; // Объявление из закрытой баз
  repres?: boolean; // От официального представителя. Только для БЦ
  owner?: boolean; // От собственника
  podSnos?: boolean; // Участие в программе реновации
  nv?: boolean; // Надёжный выбор
  mainImageid?: number; // Id заглавной фотографии на выдаче
}

/**
 * блок Partner
 */
export interface ISopr1Partner {
  FB_city?: string; // Только на выдаче и на карточке
  FB_region?: string; // Только на выдаче и на карточке
  CriteoEmail?: string;
  CriteoNewCustomer?: string;
  CriteoProducts?: ICriteoProduct[]; // RealtyID объявлений
}

/**
 * Информация о пользователе
 * @required [abGroup, type]
 */
export interface ISopr1User extends Record<string, unknown> {
  abGroup: number; // А/Б группа
  balance?: number; // Баланс
  email?: string; // Email
  numberOfObjects?: number; // Количество опубликованных (активных) объектов на сайте
  paymentType?: 'fizlico' | 'urlico'; // Тип плательщика
  type: ESopr1UserTypes; // Тип пользователя
  userId?: number; // RealtyID пользователя. Обязательно для зарегистрированного пользователя
  cianUserId?: number; // CianID пользователя. Обязательно для зарегистрированного пользователя
  registrationDate?: string; // Дата регистрации пользователя
}

/**
 * Информация о просматриваемой странице.
 * @required: [pageType, siteType]
 */
export interface ISopr1Page extends Record<string, unknown> {
  searchGuid?: string; // guid поискового запроса
  fromDeveloper?: number; // Количество объявлений от застройщика
  newObjects?: number; // Количество новостроек
  listingType?: EListingType; // Вид выдачи. Обязательно для выдачи
  sortType?: ESortType; // Тип сортировки в выдаче. Обязательно для выдачи
  pageNumber?: number; // Номер страницы в выдаче. Обязательно для выдачи
  breadCrumbs?: string[]; // Хлебные крошки
  customPageURL?: string; // Наш кастомный url, только на карточке (в т.ч. на карточках ЖК, БЦ)
  dealType: EDealType; // Тип сделки. На карточке и выдаче обязательно
  objectType: EObjectType; // Тип объекта. На карточке и выдаче обязательно
  offerID?: number; // RealtyID объявления
  offerPhone?: string; // Контактный номер владельца объявления, если их несколько, то передаем любой из них
  offersQty?: number; // Количество найденных объявлений
  queryString?: string;
  /**
   * Регион контента
   * Состоит из 3х частей: "{mo_id}-{obl_id}-{city_id}"
   * * mo_id
   *   * mo_id=0 - не Московская область (в т.ч. для г. Москва)
   *   * mo_id=1 ближнее Подмосковье
   *   * mo_id=2 дальнее Подмосковье
   *   * mo_id=3 есть объекты и ближнего и дальнего Подмосковья
   * * obl_id = актуальный Object.RegionId.
   *     Исключение - Московская область.
   *     Для нее в obl_id передавать id района, к которому относится конечный населенный пункт.
   * * city_id =  актуальный id города или если нет города, то id конечного населенного пункта.
   */
  region?: string;
  pageType?: EPageType; // Тип страницы
  siteType: ESiteType; // Тип сайта
  offerType: 'KP'; // !!! Нужно только для /pageview
  extra?: IPageViewExtra;
}

export interface ISopr1Params {
  modelVersion?: string; // Версия модели. Обязательно для событий связанных с рекомендациями, ранжированием и пр.
  sc?: {}; // search conditions
  utm?: ISopr1UTM; // Информация о UTM-метках
  page: ISopr1Page;
  products: ISopr1Product[];
  user: ISopr1User;
  partner: ISopr1Partner;
}
